import { t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { useState } from "react";

/**
 * `AA-4b` (`D-1018-AA2`, « Valider et passer à Prêt, puis choisir elle-même les banques, guidée par la recommandation »):
 * **at Prêt the chargée chooses the banks herself.** The door (`GET /dossiers/{id}/submission-options`) says which banks the
 * filter retains (recommended, each with the filter's own reason), which it excludes (each with its reason, in words), and
 * whether the gesture is open. The screen prints all of it and recomputes none of it. Her press is
 * `POST /dossiers/{id}/submissions` with the banks she ticked; a bank the filter excluded has no box.
 *
 * With the gesture closed (no verification, no eligible bank, no quotité…) the control is greyed with the door's own
 * sentence, never a live button that refuses on press (A47). With it open and no box ticked, it is greyed with the
 * door's other sentence, « aucune banque n'est cochée ».
 */
export interface SubmissionOptionsView {
  readonly stage: string;
  readonly eligibleCount: number;
  readonly retained: readonly {
    readonly bankId: string;
    readonly name: string;
    readonly reason: string;
    readonly recommended: boolean;
  }[];
  readonly excluded: readonly { readonly bankId: string; readonly name: string; readonly reason: string }[];
  /** `PRET-BANKS` (A47): the banks not on the panel, each named with its reason; no box. */
  readonly outOfPanel: readonly { readonly bankId: string; readonly name: string; readonly reason: string }[];
  /** `PRET-BANKS`: banks on the panel that cannot be sent this file (no quotité ceiling), with what they lack; no box. */
  readonly incomplete: readonly {
    readonly bankId: string;
    readonly name: string;
    readonly reason: string;
    readonly missing: readonly string[];
  }[];
  readonly gate: { readonly open: boolean; readonly reason: string | null };
}

export function SubmissionPanel(props: {
  readonly options: SubmissionOptionsView;
  readonly busy: boolean;
  readonly onSubmit: (bankIds: readonly string[]) => void;
}): React.JSX.Element {
  const { options } = props;
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const tick = (bankId: string, on: boolean): void => {
    setTicked((now) => {
      const next = new Set(now);
      if (on) next.add(bankId);
      else next.delete(bankId);
      return next;
    });
  };
  const closedReason = options.gate.open
    ? null
    : (options.gate.reason ?? t("console.soumission.aucuneCochee"));
  const noneTicked = options.gate.open && ticked.size === 0;
  return (
    <section
      data-testid="soumission-banques"
      className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <h3 className="text-sm font-semibold">{t("console.soumission.titre")}</h3>
      <p className="text-sm text-gray-700">{t("console.soumission.aide")}</p>

      {options.retained.length === 0 ? null : (
        <div data-testid="soumission-retenues">
          <p className="text-sm font-medium text-gray-700">{t("console.soumission.retenuesTitre")}</p>
          <ul className="mt-1.5 space-y-2">
            {options.retained.map((one) => (
              <li key={one.bankId}>
                <label className="flex items-start gap-3 rounded-lg px-2 py-1.5 text-sm text-gray-900 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    data-testid={`soumission-banque-${one.bankId}`}
                    checked={ticked.has(one.bankId)}
                    disabled={props.busy || !options.gate.open}
                    onChange={(event) => {
                      tick(one.bankId, event.target.checked);
                    }}
                    className="mt-1"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{one.name}</span>
                    <span className="block text-sm text-gray-700">{one.reason}</span>
                  </span>
                  {one.recommended ? (
                    <span
                      className="shrink-0 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                      data-testid={`soumission-recommandee-${one.bankId}`}
                    >
                      {t("console.soumission.recommandee")}
                    </span>
                  ) : null}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {options.excluded.length === 0 ? null : (
        <div data-testid="soumission-ecartees">
          <p className="text-sm font-medium text-gray-700">{t("console.soumission.ecarteesTitre")}</p>
          <p className="text-sm text-gray-700">{t("console.soumission.ecarteesAide")}</p>
          <ul className="mt-1.5 space-y-2">
            {options.excluded.map((one) => (
              <li key={one.bankId} data-testid={`soumission-ecartee-${one.bankId}`} className="text-sm">
                <span className="font-medium text-gray-900">{one.name}</span>
                <span className="block text-gray-700">{one.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {options.incomplete.length === 0 ? null : (
        <div data-testid="soumission-incompletes">
          <p className="text-sm font-medium text-gray-700">{t("console.soumission.incompletesTitre")}</p>
          <p className="text-sm text-gray-700">{t("console.soumission.incompletesAide")}</p>
          <ul className="mt-1.5 space-y-2">
            {options.incomplete.map((one) => (
              <li key={one.bankId} data-testid={`soumission-incomplete-${one.bankId}`} className="text-sm">
                <span className="font-medium text-gray-900">{one.name}</span>
                <span className="block text-gray-700">{one.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {options.outOfPanel.length === 0 ? null : (
        <div data-testid="soumission-hors-panel">
          <p className="text-sm font-medium text-gray-700">{t("console.soumission.horsPanelTitre")}</p>
          <p className="text-sm text-gray-700">{t("console.soumission.horsPanelAide")}</p>
          <ul className="mt-1.5 space-y-2">
            {options.outOfPanel.map((one) => (
              <li key={one.bankId} data-testid={`soumission-hors-panel-${one.bankId}`} className="text-sm">
                <span className="font-medium text-gray-900">{one.name}</span>
                <span className="block text-gray-700">{one.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {closedReason !== null ? (
        <GuardedButton
          disabled
          reason={closedReason}
          reasonTone="neutral"
          variant="primary"
          testId="dossier-soumettre-garde"
        >
          {t("console.dossier.soumettreBanques")}
        </GuardedButton>
      ) : noneTicked ? (
        <GuardedButton
          disabled
          reason={t("console.soumission.aucuneCochee")}
          reasonTone="neutral"
          variant="primary"
          testId="dossier-soumettre"
        >
          {t("console.dossier.soumettreBanques")}
        </GuardedButton>
      ) : (
        <GuardedButton
          variant="primary"
          testId="dossier-soumettre"
          onClick={() => {
            if (!props.busy) props.onSubmit([...ticked]);
          }}
        >
          {t("console.dossier.soumettreBanques")}
        </GuardedButton>
      )}
    </section>
  );
}
